/**
 * `/settings` — minimal account screen + invite-a-friend.
 */

import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { DEFAULT_USER_PREFERENCES, type Invite, type MyInvitesResponse } from '@eloquor/api-contract';
import { AppShell } from '../components/layout/AppShell';
import { grantInvite, listMyInvites } from '../api/invites';
import { useSession } from '../hooks/useSession';
import { ApiCallError } from '../api/client';

export function SettingsPage() {
  const { user, isDemo, updatePreferences } = useSession();
  const highlightColor = user?.preferences.highlightColor ?? DEFAULT_USER_PREFERENCES.highlightColor;

  async function handleHighlightColorChange(value: string) {
    try {
      await updatePreferences({ highlightColor: value });
    } catch (err) {
      // Surface real failures so a stuck PATCH is visible. The previous
      // silent-catch hid auth failures (demo sessions used to land here).
      setError(err instanceof Error ? err.message : 'Could not save preferences.');
    }
  }
  const [invites, setInvites] = useState<MyInvitesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    // Invites API requires a real (non-demo) session. Skip the call for
    // demo users so they don't see a transient 401 in devtools.
    if (isDemo) return;
    void listMyInvites().then(setInvites);
  }, [isDemo]);

  async function handleGrant(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const formEl = e.currentTarget;
    const data = new FormData(formEl);
    const email = String(data.get('email') ?? '').trim();
    const note = String(data.get('note') ?? '') || undefined;

    if (user && email.toLowerCase() === user.email.toLowerCase()) {
      setError("You can't invite yourself.");
      return;
    }

    let res;
    try {
      res = await grantInvite({ email, note });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not send invite.');
      return;
    }

    if (!res.emailSent) {
      setError(
        `Invite created but email delivery failed — share the code ${res.invite.code} manually.`
      );
    } else {
      setSuccess(`Invite sent to ${res.invite.intendedEmail ?? 'your friend'}.`);
    }
    formEl.reset();
    try {
      setInvites(await listMyInvites());
    } catch {
      // Refresh failure shouldn't poison the success banner; the next nav refetches.
    }
  }

  // For demo sessions every section is read-only: the API endpoints behind
  // them require a real (non-demo) auth context and would 401 silently.
  // Greying out + a single banner is friendlier than letting clicks fail.
  const lockedForDemo = isDemo;
  const lockedStyle: CSSProperties | undefined = lockedForDemo
    ? { opacity: 0.55, pointerEvents: 'none' }
    : undefined;

  return (
    <AppShell>
      <div className="auth-eyebrow"><span className="num">04</span> · Settings</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Account.
      </h1>

      {lockedForDemo && (
        <div
          className="banner"
          role="status"
          style={{ marginBottom: 16, maxWidth: 480 }}
          data-testid="demo-settings-banner"
        >
          <strong>You're in a demo session.</strong> Settings aren't editable — sign up for an
          account to customize preferences and invite friends.
        </div>
      )}

      {user && (
        <div className="auth-card" style={{ maxWidth: 480 }}>
          <div className="detail-row">
            <span className="k">Name</span>
            <span className="v">{user.displayName}</span>
          </div>
          <div className="detail-row">
            <span className="k">Email</span>
            <span className="v">{user.email}</span>
          </div>
          <div className="detail-row">
            <span className="k">Handle</span>
            <span className="v mono">{user.handle ? `@${user.handle}` : '—'}</span>
          </div>
          <div className="detail-row">
            <span className="k">Role</span>
            <span className="v mono">{user.role}</span>
          </div>
        </div>
      )}

      <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 18, margin: '32px 0 12px' }}>
        Reading preferences
      </h2>
      <div className="auth-card" style={{ maxWidth: 480, ...lockedStyle }} aria-disabled={lockedForDemo || undefined}>
        <div className="detail-row">
          <span className="k">Highlight color</span>
          <span className="v" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              value={highlightColor}
              disabled={lockedForDemo}
              onChange={(e) => void handleHighlightColorChange(e.currentTarget.value)}
              style={{
                width: 36,
                height: 24,
                padding: 0,
                border: '1px solid var(--border)',
                borderRadius: 4,
                background: 'transparent',
                cursor: lockedForDemo ? 'not-allowed' : 'pointer',
              }}
              aria-label="Highlight color"
            />
            <span className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
              {highlightColor.toUpperCase()}
            </span>
          </span>
        </div>
      </div>

      <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 18, margin: '32px 0 12px' }}>
        Invite a friend
      </h2>
      <p style={{ fontSize: 14, color: 'var(--fg-2)', marginBottom: 16, ...lockedStyle }}>
        You have{' '}
        <strong style={{ color: 'var(--fg-1)' }}>{invites?.available ?? 0}</strong> invite
        {invites?.available === 1 ? '' : 's'} to give.
      </p>

      <form
        className="auth-form"
        onSubmit={handleGrant}
        style={{ maxWidth: 480, ...lockedStyle }}
        aria-disabled={lockedForDemo || undefined}
      >
        <div className="field">
          <label className="label" htmlFor="email">
            Friend's email
          </label>
          <input
            className="input"
            type="email"
            id="email"
            name="email"
            placeholder="friend@school.edu"
            required
            disabled={lockedForDemo}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="note">
            Personal note (optional)
          </label>
          <textarea
            className="input"
            id="note"
            name="note"
            rows={3}
            maxLength={280}
            placeholder="You'll love this for prep…"
            disabled={lockedForDemo}
          />
        </div>
        {error && <div className="helper err"><span className="dot" />{error}</div>}
        {success && <div className="helper ok"><span className="dot" />{success}</div>}
        <button
          className="btn-primary"
          type="submit"
          disabled={lockedForDemo || (invites?.available ?? 0) <= 0}
        >
          Send invite <span aria-hidden>→</span>
        </button>
      </form>

      {invites && invites.history.length > 0 && (
        <>
          <h3 style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 14, margin: '32px 0 8px', color: 'var(--fg-2)' }}>
            Past invites
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {invites.history.map((i: Invite) => (
                <tr key={i.code} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--font-mono)' }}>{i.code}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--fg-3)' }}>{i.intendedEmail ?? '—'}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--fg-3)' }}>
                    {i.redeemedAt ? 'redeemed' : i.revokedAt ? 'revoked' : 'pending'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </AppShell>
  );
}
